// The Runs view (design 10): station run history across every order, plus the
// one standalone invocation v1 allows - the read-only oracles. The history is
// how an operator answers "what has the line actually been doing" without
// opening every order; the oracle box is the independent re-check ("does
// Google/Cloudflare/Instantly agree with the stamps") that used to be
// curl-only.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BuildShowToast } from './BuildLine';
import { BUILD_ORACLE_KINDS, buildApi, buildErrorMessage, type BuildStationRunListRow } from './buildApi';
import { ago, fmtDate } from './Orders';

const POLL_MS = 10_000;

/** Compact one-line rendering of a run's results: the scalar counters only,
 *  which is what every station writes its headline numbers as. The full JSON
 *  rides the title attribute for the rows where the counters are not enough. */
function summarize(results: Record<string, unknown> | null): string {
  if (!results) return '-';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(results)) {
    if (typeof value === 'number' || typeof value === 'boolean') parts.push(`${key}=${value}`);
    else if (typeof value === 'string' && value.length <= 24) parts.push(`${key}=${value}`);
    else if (Array.isArray(value)) parts.push(`${key}[${value.length}]`);
  }
  return parts.slice(0, 6).join(' ') || 'recorded';
}

function durationOf(run: BuildStationRunListRow): string {
  if (!run.finished_at) return 'running';
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '-';
  if (ms < 1000) return '<1s';
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

/** The one budget nothing draws from yet (the deferred multi-order browser
 *  admission) - shown honestly as unenforced rather than a reassuring 0/2. */
const UNENFORCED_GOVERNORS = new Set(['browser-orders']);

export function Runs({ showToast }: { showToast: BuildShowToast }): JSX.Element {
  const [runs, setRuns] = useState<BuildStationRunListRow[]>([]);
  const [governors, setGovernors] = useState<{ key: string; in_use: number | null; limit: number }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stationFilter, setStationFilter] = useState('');
  const [orderText, setOrderText] = useState('');

  const [oracleOrderText, setOracleOrderText] = useState('');
  const [oracleKind, setOracleKind] = useState<string>(BUILD_ORACLE_KINDS[0]);
  const [oracleBusy, setOracleBusy] = useState(false);
  const [oracleAnswer, setOracleAnswer] = useState<{ pass: boolean; detail: unknown } | null>(null);

  const orderId = /^\d+$/.test(orderText.trim()) ? Number(orderText.trim()) : undefined;
  const oracleOrderId = /^\d+$/.test(oracleOrderText.trim()) ? Number(oracleOrderText.trim()) : null;

  /** The poll reads the CURRENT filters off a ref, so changing a filter does
   *  not have to tear the interval down and back up. */
  const filters = useRef({ orderId, station: stationFilter });
  filters.current = { orderId, station: stationFilter };

  const refresh = useCallback(async () => {
    try {
      const [rows, gauges] = await Promise.all([
        buildApi.listRuns({
          ...(filters.current.orderId ? { orderId: filters.current.orderId } : {}),
          ...(filters.current.station ? { station: filters.current.station } : {}),
        }),
        // Best effort beside the history: a gauge that cannot be read must not
        // cost the run list its refresh.
        buildApi.listGovernors().catch(() => [] as { key: string; in_use: number | null; limit: number }[]),
      ]);
      setRuns(rows);
      if (gauges.length > 0) setGovernors(gauges);
      setLoadError(null);
    } catch (err) {
      setLoadError(buildErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  // Filter changes refresh immediately rather than waiting out the poll lap.
  useEffect(() => {
    void refresh();
  }, [refresh, orderId, stationFilter]);

  const stations = [...new Set(runs.map((r) => r.station))].sort();

  async function askOracle(): Promise<void> {
    if (oracleOrderId === null) return;
    setOracleBusy(true);
    setOracleAnswer(null);
    try {
      const result = await buildApi.runOracle(oracleOrderId, oracleKind);
      setOracleAnswer(result);
      showToast(
        result.pass ? 'success' : 'warn',
        `Oracle ${oracleKind} on order #${oracleOrderId}: ${result.pass ? 'PASS' : 'FAIL'}`,
      );
      void refresh();
    } catch (err) {
      showToast('error', `Oracle refused: ${buildErrorMessage(err)}`);
    } finally {
      setOracleBusy(false);
    }
  }

  return (
    <>
      {governors.length > 0 && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">Governors</h2>
            <span className="bl-hint">
              The shared budgets every station draws from - the multi-order safety rails. A full
              bucket is why everything is suddenly patient, not broken.
            </span>
          </div>
          <div className="bl-card-body">
            <div className="bl-formgrid">
              {governors.map((g) => (
                <div className="bl-field" key={g.key}>
                  <span className="bl-field-label">{g.key}</span>
                  <span className={`bl-readout ${g.in_use !== null && g.in_use >= g.limit ? 'bl-readout--bad' : ''}`}>
                    {UNENFORCED_GOVERNORS.has(g.key)
                      ? `defined, not enforced yet (budget ${g.limit})`
                      : g.in_use === null
                        ? `unreadable / ${g.limit}`
                        : `${g.in_use} of ${g.limit} in use`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title">Ask an oracle</h2>
          <span className="bl-hint">
            Read-only by construction: an oracle re-checks the stamps against the independent
            source (Google, Cloudflare, the sequencer, MasterInbox) and writes nothing. Safe
            beside a live order.
          </span>
        </div>
        <div className="bl-card-body">
          <div className="bl-formgrid">
            <div className="bl-field">
              <label className="bl-field-label" htmlFor="bl-runs-order">Order id</label>
              <input
                id="bl-runs-order"
                className="bl-input bl-mono"
                inputMode="numeric"
                value={oracleOrderText}
                onChange={(e) => setOracleOrderText(e.target.value)}
                placeholder="42"
              />
            </div>
            <div className="bl-field">
              <label className="bl-field-label" htmlFor="bl-runs-kind">Oracle</label>
              <select
                id="bl-runs-kind"
                className="bl-input"
                value={oracleKind}
                onChange={(e) => setOracleKind(e.target.value)}
              >
                {BUILD_ORACLE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>{kind}</option>
                ))}
              </select>
            </div>
            <div className="bl-field">
              <span className="bl-field-label">&nbsp;</span>
              <button
                type="button"
                className="bl-btn"
                disabled={oracleOrderId === null || oracleBusy}
                onClick={() => void askOracle()}
              >
                {oracleBusy ? 'Asking...' : 'Run oracle'}
              </button>
            </div>
          </div>
          {oracleAnswer !== null && (
            <div className={`bl-notice bl-notice--${oracleAnswer.pass ? 'ok' : 'warn'} bl-notice--standalone`}>
              <strong>{oracleAnswer.pass ? 'PASS' : 'FAIL'}.</strong>
              <details className="bl-details">
                <summary>Detail</summary>
                <pre className="bl-details-body bl-mono">
                  {JSON.stringify(oracleAnswer.detail, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </section>

      <section className="bl-card">
        <div className="bl-card-header">
          <h2 className="bl-card-title">Station runs</h2>
          <span className="bl-hint">
            Every invocation, coordinator-fired or standalone, newest first. Refreshes every 10
            seconds.
          </span>
          <div className="bl-actions-spacer" />
          <input
            className="bl-input bl-input--filter bl-mono"
            inputMode="numeric"
            aria-label="Filter by order id"
            placeholder="order id"
            value={orderText}
            onChange={(e) => setOrderText(e.target.value)}
          />
          <select
            className="bl-input bl-input--filter"
            aria-label="Filter by station"
            value={stationFilter}
            onChange={(e) => setStationFilter(e.target.value)}
          >
            <option value="">All stations</option>
            {stations.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        {loadError !== null && (
          <div className="bl-notice bl-notice--err bl-notice--standalone">
            <strong>The last refresh failed.</strong> {loadError}.
          </div>
        )}
        <div className="bl-table-wrap">
          <table className="bl-table">
            <thead>
              <tr>
                <th>Station</th>
                <th>Order</th>
                <th>By</th>
                <th className="bl-col-center">Started</th>
                <th className="bl-col-center">Took</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="bl-empty">
                    No station runs recorded{orderId || stationFilter ? ' for that filter' : ' yet'}.
                  </td>
                </tr>
              ) : (
                runs.map((run) => (
                  <tr key={run.id}>
                    <td className="bl-cell-mono">{run.station}</td>
                    <td className="bl-cell-faint">
                      {run.order_id !== null
                        ? `${run.order_label ?? 'order'} #${run.order_id}`
                        : '-'}
                    </td>
                    <td className="bl-cell-faint">{run.initiated_by}</td>
                    <td className="bl-col-center bl-cell-faint" title={fmtDate(run.started_at)}>
                      {ago(run.started_at)}
                    </td>
                    <td className="bl-col-center bl-num">{durationOf(run)}</td>
                    <td
                      className="bl-cell-faint bl-cell-trunc"
                      title={run.results ? JSON.stringify(run.results, null, 2) : ''}
                    >
                      {summarize(run.results)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
